// --- Opening hours helpers ---

const DAY_TO_SCHEMA: Record<string, string> = {
  mon: 'Mo',
  tue: 'Tu',
  wed: 'We',
  thu: 'Th',
  fri: 'Fr',
  sat: 'Sa',
  sun: 'Su',
};

type DayHours = {
  openFrom: string | null;
  openTo: string | null;
};

type BranchOpeningHours = Partial<Record<string, DayHours>>;

function formatTime(time: string): string {
  if (time.includes('T')) {
    const parts = time.split('T')[1]?.split(':');
    return parts ? `${parts[0]}:${parts[1]}` : time.substring(0, 5);
  }
  return time.substring(0, 5);
}

function branchOpeningHoursToSchema(
  openingHours: BranchOpeningHours,
): string[] {
  const result: string[] = [];
  for (const [dayKey, schemaDay] of Object.entries(DAY_TO_SCHEMA)) {
    const day = openingHours[dayKey];
    if (day?.openFrom && day?.openTo) {
      result.push(
        `${schemaDay} ${formatTime(day.openFrom)}-${formatTime(day.openTo)}`,
      );
    }
  }
  return result;
}

// --- Nutrition helpers ---

interface NutritionalValues {
  energyKcal?: number | null;
  protein?: number | null;
  carbohydrate?: number | null;
  fat?: number | null;
  sugar?: number | null;
  salt?: number | null;
  fiber?: number | null;
  saturatedFattyAcid?: number | null;
}

function buildNutritionSchema(nv: NutritionalValues): Record<string, string> {
  const nutrition: Record<string, string> = { '@type': 'NutritionInformation' };
  if (nv.energyKcal != null) nutrition.calories = `${nv.energyKcal} Cal`;
  if (nv.fat != null) nutrition.fatContent = `${nv.fat} g`;
  if (nv.saturatedFattyAcid != null)
    nutrition.saturatedFatContent = `${nv.saturatedFattyAcid} g`;
  if (nv.carbohydrate != null)
    nutrition.carbohydrateContent = `${nv.carbohydrate} g`;
  if (nv.sugar != null) nutrition.sugarContent = `${nv.sugar} g`;
  if (nv.protein != null) nutrition.proteinContent = `${nv.protein} g`;
  if (nv.fiber != null) nutrition.fiberContent = `${nv.fiber} g`;
  // salt in g → sodium in mg (1 g salt ≈ 400 mg sodium)
  if (nv.salt != null)
    nutrition.sodiumContent = `${Math.round(nv.salt * 400)} mg`;
  return nutrition;
}

// --- Shared interfaces ---

export interface BranchSchemaData {
  name: string;
  address: string;
  city: string;
  openingHours: BranchOpeningHours;
  location: [number, number];
  phoneNumber?: string | null;
  imagePath?: string | null;
}

export interface ProductSchemaData {
  title: string;
  description?: string | null;
  shortDescription?: string | null;
  slug: string;
  image?: string | null;
  nutritionalValues?: NutritionalValues | null;
  tags?: ReadonlyArray<{ name: string; id: string }>;
  /** Price in smallest currency unit (haléře). Divide by 100 to get CZK. */
  priceHalere?: number | null;
  available?: boolean;
  productUrl?: string | null;
}

export interface MenuSectionData {
  name: string;
  products: ProductSchemaData[];
}

// --- Brand config ---

export interface RestaurantSchemaConfig {
  baseUrl: string;
  logo: string;
  servesCuisine: readonly string[];
}

// --- Schema builders ---

const DIET_TAG_MAP: Record<string, string> = {
  vegetarian: 'https://schema.org/VegetarianDiet',
  veg: 'https://schema.org/VeganDiet',
  vege: 'https://schema.org/VegetarianDiet',
  vegan: 'https://schema.org/VeganDiet',
  'gluten-free': 'https://schema.org/GlutenFreeDiet',
  glutenfree: 'https://schema.org/GlutenFreeDiet',
  lactosefree: 'https://schema.org/LowLactoseDiet',
};

export function buildFastFoodRestaurantSchema(
  branch: BranchSchemaData,
  config: RestaurantSchemaConfig,
): Record<string, unknown> {
  const { baseUrl, logo, servesCuisine } = config;
  const [lng, lat] = branch.location;
  return {
    '@context': 'https://schema.org',
    '@type': 'FastFoodRestaurant',
    name: branch.name,
    parentOrganization: { '@id': `${baseUrl}/#organization` },
    image: branch.imagePath ?? logo,
    url: `${baseUrl}/shipping/courier`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: branch.address,
      addressLocality: branch.city,
      addressCountry: 'CZ',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lng,
    },
    ...(branch.phoneNumber ? { telephone: branch.phoneNumber } : {}),
    openingHours: branchOpeningHoursToSchema(branch.openingHours),
    servesCuisine,
    priceRange: '$$',
    menu: `${baseUrl}/menu`,
    currenciesAccepted: 'CZK',
    paymentAccepted: 'Credit Card, Debit Card',
    potentialAction: {
      '@type': 'OrderAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/shipping/courier`,
        inLanguage: 'cs',
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform',
        ],
      },
      deliveryMethod: [
        'http://purl.org/goodrelations/v1#DeliveryModeToDoorAddress',
      ],
    },
  };
}

export function buildMenuItemSchema(
  product: ProductSchemaData,
  productUrl: string,
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    '@type': 'MenuItem',
    name: product.title,
    description: product.description ?? product.shortDescription ?? '',
    url: productUrl,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'CZK',
      ...(product.priceHalere != null
        ? { price: (product.priceHalere / 100).toFixed(2) }
        : {}),
      availability:
        product.available !== false
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
    },
  };

  if (product.image) schema.image = product.image;

  if (product.nutritionalValues) {
    schema.nutrition = buildNutritionSchema(product.nutritionalValues);
  }

  const dietValues = product.tags
    ?.map((t) => DIET_TAG_MAP[t.id] ?? DIET_TAG_MAP[t.name?.toLowerCase()])
    .filter(Boolean) as string[] | undefined;

  if (dietValues?.length) {
    schema.suitableForDiet =
      dietValues.length === 1 ? dietValues[0] : dietValues;
  }

  return schema;
}

export function buildMenuSchema(
  sections: MenuSectionData[],
  menuUrl: string,
  menuName: string,
  websiteId: string,
  productUrlBuilder: (
    product: ProductSchemaData,
    section: MenuSectionData,
  ) => string,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': ['WebPage', 'Menu'],
    url: menuUrl,
    name: menuName,
    isPartOf: { '@id': websiteId },
    inLanguage: 'cs',
    hasMenuSection: sections.map((section) => ({
      '@type': 'MenuSection',
      name: section.name,
      hasMenuItem: section.products.map((p) =>
        buildMenuItemSchema(p, productUrlBuilder(p, section)),
      ),
    })),
  };
}
